import React, { useState } from 'react';
import { Modal, Upload, Button, message, Alert, Typography, Space, Divider } from 'antd';
import { UploadOutlined, DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import type { AxiosResponse } from 'axios';
import { apiService } from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';

const { Title, Paragraph, Text } = Typography;

interface BulkImportModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  clubId: number;
}

const BulkImportModal: React.FC<BulkImportModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  clubId,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  const handleDownloadTemplate = async () => {
    try {
      const response: AxiosResponse<Blob> = await apiService.get(
        API_ENDPOINTS.PARTICIPANTS.BULK_UPLOAD_TEMPLATE,
        { responseType: 'blob' }
      ) as AxiosResponse<Blob>;

      // Явно указываем, что response.data это Blob
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_participants.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success('Шаблон успешно скачан');
    } catch (error) {
      logger.error('Error downloading template:', error);
      message.error('Ошибка при скачивании шаблона');
    }
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Выберите файл для загрузки');
      return;
    }

    // Получаем файл из fileList - может быть originFileObj или сам файл
    const uploadFile = fileList[0];
    const file = uploadFile.originFileObj || (uploadFile as any).file || uploadFile;
    
    if (!file || !(file instanceof File)) {
      message.warning('Файл не выбран или имеет неверный формат');
      return;
    }

    try {
      setUploading(true);
      setImportResult(null);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('clubId', clubId.toString());

      const response = await apiService.post<{ success: boolean; data: any; message?: string }>(
        API_ENDPOINTS.PARTICIPANTS.BULK_UPLOAD_EXCEL,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        const result = response.data.data;
        setImportResult(result);
        
        if (result.failed === 0) {
          message.success(`Успешно импортировано ${result.success} участников`);
          setTimeout(() => {
            onSuccess();
            handleCancel();
          }, 2000);
        } else {
          message.warning(
            `Импортировано ${result.success} участников, ${result.failed} с ошибками`
          );
        }
      } else {
        message.error('Ошибка при импорте участников');
      }
    } catch (error: any) {
      logger.error('Error uploading file:', error);
      const errorMessage = error.response?.data?.message || 'Ошибка при загрузке файла';
      message.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setFileList([]);
    setImportResult(null);
    onCancel();
  };

  const uploadProps = {
    fileList,
    beforeUpload: (file: File) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     file.type === 'application/vnd.ms-excel' ||
                     file.name.endsWith('.xlsx') ||
                     file.name.endsWith('.xls');
      
      if (!isExcel) {
        message.error('Только Excel файлы (.xlsx, .xls) разрешены');
        return false;
      }

      // Создаем объект UploadFile с originFileObj
      const uploadFile: UploadFile = {
        uid: `${Date.now()}`,
        name: file.name,
        status: 'done',
        originFileObj: file as any, // File совместим с RcFile для наших целей
      };

      setFileList([uploadFile]);
      return false; // Предотвращаем автоматическую загрузку
    },
    onRemove: () => {
      setFileList([]);
      setImportResult(null);
    },
    maxCount: 1,
  };

  return (
    <Modal
      title="Массовый импорт спортсменов"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={700}
      destroyOnClose
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={5}>
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            Инструкция по заполнению
          </Title>
          <Paragraph>
            Для массового импорта спортсменов необходимо:
          </Paragraph>
          <ul>
            <li>Скачать шаблон Excel файла</li>
            <li>Заполнить обязательные поля: <Text strong>Фамилия</Text> и <Text strong>Имя</Text></li>
            <li>Заполнить дополнительные поля (необязательно): Отчество, Email, Телефон, Дата рождения, Пол, Вес, Уровень пояса, ID категории</li>
            <li>Загрузить заполненный файл</li>
          </ul>
          <Divider />
          <Paragraph>
            <Text strong>Формат даты:</Text> ДД.ММ.ГГГГ (например, 15.01.2000)
          </Paragraph>
          <Paragraph>
            <Text strong>Пол:</Text> male (мужской) или female (женский)
          </Paragraph>
          <Paragraph>
            <Text strong>Уровень пояса:</Text> например, 11_kyu, 10_kyu, 1_kyu, 1_dan и т.д.
          </Paragraph>
        </div>

        <div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadTemplate}
            block
          >
            Скачать шаблон Excel
          </Button>
        </div>

        <div>
          <Title level={5}>Загрузка файла</Title>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Выбрать файл</Button>
          </Upload>
          {fileList.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text>Выбранный файл: {fileList[0].name}</Text>
            </div>
          )}
        </div>

        {importResult && (
          <div>
            <Alert
              message={`Импорт завершен: ${importResult.success} успешно, ${importResult.failed} с ошибками`}
              type={importResult.failed === 0 ? 'success' : 'warning'}
              showIcon
            />
            {importResult.errors && importResult.errors.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Ошибки:</Title>
                {importResult.errors.slice(0, 10).map((error: any, index: number) => (
                  <Alert
                    key={index}
                    message={`Строка ${error.row}: ${error.error}`}
                    type="error"
                    style={{ marginBottom: 8 }}
                  />
                ))}
                {importResult.errors.length > 10 && (
                  <Text type="secondary">
                    ... и еще {importResult.errors.length - 10} ошибок
                  </Text>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ textAlign: 'right' }}>
          <Button onClick={handleCancel} style={{ marginRight: 8 }}>
            Отмена
          </Button>
          <Button
            type="primary"
            onClick={handleUpload}
            loading={uploading}
            disabled={fileList.length === 0}
          >
            Загрузить
          </Button>
        </div>
      </Space>
    </Modal>
  );
};

export default BulkImportModal;

